import type { Edulead } from 'edulead';
import { useCallback, useEffect, useState } from 'react';
import { FeedTable } from '../../custom-components/FeedTable';
import LoadingBars from '../../custom-components/LoadingBars';
import { eduleadV4 } from '../../services/edulead-v4-client';
import { ActivityFeedItem } from '../../services/pusher.service';
import { toActivityFeedItem } from '../../utils/feedItem';
import './PersonDetailActivity.scss';

const INITIAL_LOAD = 25;
const LOAD_MORE_BATCH = 25;

// The observation time, not the record's: this list is ordered by when the source saw it, and the
// timestamp shown has to match the order it is shown in.
const activityItem = (personId: string) => (item: Edulead.EduleadV4PersonActivity) =>
  toActivityFeedItem(item, personId, item.observe_time ?? '');

interface PersonDetailActivityProps {
  personId: string;
}

export function PersonDetailActivity({ personId }: PersonDetailActivityProps) {
  const [items, setItems] = useState<ActivityFeedItem[]>([]);
  const [nextPageToken, setNextPageToken] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setIsLoading(true);
      try {
        const page = await eduleadV4.people.listActivity({
          person_id: personId,
          page_size: INITIAL_LOAD,
        });
        // The person page remounts this on navigation between contacts; a slow response
        // for the previous contact must not overwrite the current one's list.
        if (cancelled) return;
        setItems(page.data.map(activityItem(personId)));
        setNextPageToken(page.response.next_page_token ?? '');
      } catch (err) {
        if (!cancelled) console.error('[PersonDetailActivity] Failed to load activity:', err);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [personId]);

  const loadMore = useCallback(async () => {
    if (!nextPageToken || isLoadingMore) return;
    setIsLoadingMore(true);
    try {
      const page = await eduleadV4.people.listActivity({
        person_id: personId,
        page_size: LOAD_MORE_BATCH,
        page_token: nextPageToken,
      });
      setItems((prev) => [...prev, ...page.data.map(activityItem(personId))]);
      setNextPageToken(page.response.next_page_token ?? '');
    } catch (err) {
      console.error('[PersonDetailActivity] Failed to load more:', err);
    } finally {
      setIsLoadingMore(false);
    }
  }, [personId, nextPageToken, isLoadingMore]);

  if (isLoading) {
    return (
      <div className="person-detail-activity">
        <h3 className="person-detail-activity__title">Activity</h3>
        <LoadingBars numberOfRows={3} barHeight={60} />
      </div>
    );
  }

  // FeedTable renders nothing for an empty list, which would leave a bare heading behind.
  // Most contacts have no qualifying events at all, so the whole section stands down.
  if (items.length === 0) {
    return null;
  }

  return (
    <div className="person-detail-activity">
      <h3 className="person-detail-activity__title">Activity</h3>
      <FeedTable
        items={items}
        hideContact
        hasMore={nextPageToken !== ''}
        isLoadingMore={isLoadingMore}
        onLoadMore={loadMore}
      />
    </div>
  );
}
